import type { ReactNode } from 'react';

import type { MenuItemState } from '~/chords/KebabMenu/components/KebabMenuItem/KebabMenuItem';
import type { RenderDropdownProps } from '~/chords/MultiSelect/MultiSelect';
import { useMultiSelect } from '~/chords/MultiSelect/useMultiSelect';

import { SelectedItemRows } from './components/SelectedItemRows';

export type { RenderDropdownProps };

export interface SortableMultiSelectSecondaryAction<T> {
  label: string;
  onAction: (item: T) => void;
  state?: MenuItemState;
}

export interface SortableMultiSelectProps<T> {
  value: T[];
  onValueChange: (items: T[]) => void;
  getItemLabel: (item: T) => string;
  getItemValue: (item: T) => string;
  getItemImage?: (item: T) => string | undefined;
  renderContent?: (item: T) => ReactNode;
  secondaryActions?: SortableMultiSelectSecondaryAction<T>[];
  renderDropdown: (props: RenderDropdownProps<T>) => ReactNode;
  removeLabel?: string;
  disabled?: boolean;
}

export function SortableMultiSelect<T>({
  value,
  onValueChange,
  getItemLabel,
  getItemValue,
  getItemImage,
  renderContent,
  secondaryActions,
  renderDropdown,
  removeLabel,
  disabled = false,
}: SortableMultiSelectProps<T>) {
  const { selectedIds, resetKey, handleSelect, handleRemove } = useMultiSelect({
    selectedValues: value,
    onValueChange,
    getItemValue,
    disabled,
  });

  const handleReorder = (reorderedItems: T[]) => {
    if (disabled) {
      return;
    }

    onValueChange(reorderedItems);
  };

  const resolvedSecondaryActions = secondaryActions
    ? (item: T) =>
        secondaryActions.map((action) => ({
          label: action.label,
          onAction: () => action.onAction(item),
          state: action.state,
        }))
    : undefined;

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex justify-end">
        {renderDropdown({
          onSelect: handleSelect,
          selectedValues: selectedIds,
          resetKey,
          disabled,
        })}
      </div>
      <SelectedItemRows<T>
        items={value}
        getItemValue={getItemValue}
        getItemLabel={getItemLabel}
        getItemImage={getItemImage}
        renderContent={renderContent}
        secondaryActions={resolvedSecondaryActions}
        onRemove={handleRemove}
        onReorder={handleReorder}
        removeLabel={removeLabel}
        disabled={disabled}
      />
    </div>
  );
}

SortableMultiSelect.displayName = 'SortableMultiSelect';
